/**
 * Sibling Product Switcher (with hover-prefetch + cache)
 *
 * Color swatches on Chivio link to separate "sibling" products — one
 * Shopify product per color, connected via metaobject. Clicking them
 * triggers a full page navigation by default.
 *
 * This script intercepts those clicks and does a manual fetch + DOM swap:
 *   1. Fetch the sibling's product section HTML via Shopify's Section
 *      Rendering API (?section_id=…).
 *   2. Parse the response and find the new product-info element.
 *   3. Replace the current product-info with the new one.
 *   4. Update the browser URL via history.replaceState.
 *
 * **Speed strategy** (the reason for the recent rewrite):
 *   - Hover on a swatch fires a fetch IMMEDIATELY. By the time the user
 *     clicks ~200-500ms later, the HTML is cached and the swap takes
 *     ~0ms of network time.
 *   - On idle after page load, all sibling URLs are prefetched in the
 *     background, so even a first-click without hover is instant.
 *   - Prefetch also warms up the first gallery image via new Image().
 *     The browser caches it, so the swapped DOM paints immediately.
 *   - Click flow uses the cached HTML, falls back to live fetch only on
 *     cache miss / failure.
 *   - Animation delays trimmed: fade-out floor 140ms → 60ms, image preload
 *     safety 700ms → 200ms (with cache, usually 0ms).
 *
 * By doing the swap ourselves (not through Foxify's renderProductInfo) we
 * avoid Foxify's loading overlay that was causing a stuck white-screen.
 * Foxify's connectedCallback still fires on the new product-info when it
 * enters the DOM, so Flickity, payment buttons, etc. all reinitialize.
 */

(function () {
  'use strict';

  const LOG_PREFIX = '[sibling-switcher]';
  let swapping = false;
  // pathname -> Promise<{html: string}>
  const cache = new Map();

  function findActiveProductInfo() {
    const candidates = document.querySelectorAll('[id^="productTemplate--"], [id^="MainProduct-"]');
    for (let i = 0; i < candidates.length; i++) {
      const style = candidates[i].getAttribute('style') || '';
      if (style.indexOf('display: none') === -1 && style.indexOf('display:none') === -1) {
        return candidates[i];
      }
    }
    return candidates[0] || null;
  }

  function getSectionId(el) {
    if (!el) return null;
    const m = (el.id || '').match(/^(?:productTemplate--|MainProduct-)(.+)$/);
    if (m) return m[1];
    if (el.sectionId) return el.sectionId;
    return null;
  }

  function pathnameOf(href) {
    try { return new URL(href, window.location.origin).pathname; }
    catch { return null; }
  }

  // Fire-and-forget image preload — warms browser cache so the eventual
  // DOM swap paints with no flash.
  function warmImage(srcOrSrcset) {
    if (!srcOrSrcset) return;
    const src = srcOrSrcset.indexOf(',') >= 0
      ? srcOrSrcset.split(',')[0].trim().split(' ')[0]
      : srcOrSrcset.trim().split(' ')[0];
    if (!src) return;
    const probe = new Image();
    probe.src = src;
  }

  // Prefetch a sibling. Returns a Promise<string> resolving to HTML.
  // Subsequent calls for the same pathname return the same Promise.
  function prefetch(pathname, sectionId) {
    if (!pathname || !sectionId) return null;
    if (cache.has(pathname)) return cache.get(pathname);

    const url = pathname + '?section_id=' + sectionId;
    const p = fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('fetch ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // Warm the browser image cache for the first gallery image so
        // the swap paints with no flash. Foxify's <lazy-image> swaps
        // a placeholder `src` for a real one only after intersection,
        // so we look at data-src and data-srcset too.
        try {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          // Warm every gallery image (capped at 6) — warming only the first
          // left the remaining slides visibly lazy-loading after the swap.
          const imgs = doc.querySelectorAll(
            '.product-gallery__media img, .product-gallery__media-slide img, ' +
            '.product__media img, [id^="productTemplate--"] img, [id^="MainProduct-"] img'
          );
          for (let i = 0; i < Math.min(imgs.length, 6); i++) {
            const img = imgs[i];
            warmImage(img.getAttribute('src'));
            warmImage(img.getAttribute('srcset'));
            warmImage(img.getAttribute('data-src'));
            warmImage(img.getAttribute('data-srcset'));
          }
        } catch (e) { /* non-fatal */ }
        return html;
      });

    cache.set(pathname, p);
    // Evict on failure so a subsequent click can retry.
    p.catch(function () { cache.delete(pathname); });
    return p;
  }

  // Warm the swatch's instant-preview image. CRITICAL: the click handler
  // sets `mainImg.src = data-sibling-image` (a width:1400 URL) for the
  // instant swap — but that exact URL differs from the gallery images
  // warmed via the section HTML (which carry their own widths, e.g. 848).
  // Without warming this precise URL the "instant" preview actually has
  // to download a fresh 1400px image on click → the visible delay the
  // client reported. Warming it here means it's decoded and in cache
  // before the click lands.
  function warmSwatchPreview(link) {
    if (!link) return;
    warmImage(link.getAttribute('data-sibling-image'));
  }

  // Hover-prefetch: when the cursor enters a swatch link, kick off the
  // fetch. mouseenter doesn't bubble so we use `pointerover` on document
  // (which DOES bubble) and check if the target is a swatch link.
  document.addEventListener('pointerover', function (e) {
    const link = e.target.closest && e.target.closest('.siblings a[href]');
    if (!link) return;
    if (link.getAttribute('aria-current') === 'true') return;
    warmSwatchPreview(link);
    const pathname = pathnameOf(link.getAttribute('href'));
    if (!pathname) return;
    const sectionId = getSectionId(findActiveProductInfo());
    if (!sectionId) return;
    prefetch(pathname, sectionId);
  }, { passive: true, capture: true });

  // Touch-prefetch: on mobile, touchstart fires before click. Same idea.
  document.addEventListener('touchstart', function (e) {
    const link = e.target.closest && e.target.closest('.siblings a[href]');
    if (!link) return;
    if (link.getAttribute('aria-current') === 'true') return;
    warmSwatchPreview(link);
    const pathname = pathnameOf(link.getAttribute('href'));
    if (!pathname) return;
    const sectionId = getSectionId(findActiveProductInfo());
    if (!sectionId) return;
    prefetch(pathname, sectionId);
  }, { passive: true, capture: true });

  // Eager prefetch all siblings on idle so even the first click — without
  // any hover — is instant.
  function eagerPrefetchAll() {
    const sectionId = getSectionId(findActiveProductInfo());
    if (!sectionId) return;
    const links = document.querySelectorAll('.siblings a[href]');
    let i = 0;
    function step() {
      if (i >= links.length) return;
      const link = links[i++];
      if (link.getAttribute('aria-current') !== 'true') {
        warmSwatchPreview(link); // warm the instant-preview image too
        const pathname = pathnameOf(link.getAttribute('href'));
        if (pathname) prefetch(pathname, sectionId);
      }
      // Stagger so we don't saturate the network on slow connections
      setTimeout(step, 120);
    }
    step();
  }

  function scheduleEagerPrefetch() {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(eagerPrefetchAll, { timeout: 2500 });
    } else {
      setTimeout(eagerPrefetchAll, 1000);
    }
  }

  // Run after initial paint settles.
  if (document.readyState === 'complete') {
    scheduleEagerPrefetch();
  } else {
    window.addEventListener('load', scheduleEagerPrefetch, { once: true });
  }

  // Click handler — uses cache when available.
  document.addEventListener('click', function (e) {
    const link = e.target.closest('.siblings a');
    if (!link) return;

    if (link.getAttribute('aria-current') === 'true') {
      e.preventDefault();
      return;
    }

    const href = link.getAttribute('href');
    if (!href || href === '#' || href === '#!') return;

    const pathname = pathnameOf(href);
    if (!pathname) return;

    e.preventDefault();

    if (swapping) return;

    const productInfo = findActiveProductInfo();
    const sectionId = getSectionId(productInfo);

    if (!productInfo || !sectionId) {
      console.warn(LOG_PREFIX, 'no product-info / sectionId, hard nav');
      window.location.href = pathname;
      return;
    }

    swapping = true;
    document.querySelector('.chivio-pdp-colour-preview')?.remove();

    // -- INSTANT VISUAL FEEDBACK --
    // 1. Instantly update active swatch state
    const allSiblings = document.querySelectorAll('.siblings a');
    allSiblings.forEach(a => {
      if (a === link) {
        a.setAttribute('aria-current', 'true');
        const icon = a.querySelector('.icon-check');
        if (!icon && !a.querySelector('img')) {
          const check = document.createElement('span');
          check.className = 'icon icon-check';
          a.querySelector('tooltip-component')?.insertBefore(check, a.querySelector('.visually-hidden'));
        }
      } else {
        a.removeAttribute('aria-current');
        a.querySelector('.icon-check')?.remove();
      }
    });

    // 2. Instantly preview the new product image
    const previewImage = link.getAttribute('data-sibling-image');
    if (previewImage) {
      const mainImg = productInfo.querySelector(
        '.product-gallery__media img, .product-gallery__media-slide img, .product__media img'
      );
      if (mainImg) {
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('data-srcset');
        mainImg.src = previewImage;
      }
    }

    // Removed the opacity fade-out so the text swap feels totally instant without a "loading" state.

    // Use cached promise if available; otherwise live-fetch.
    const htmlPromise = cache.get(pathname) || prefetch(pathname, sectionId);

    // Wait for the first gallery image to be in browser cache before
    // swapping. With prefetch warm-up this is usually already true,
    // resolving in 0ms. Hard cap at 250ms so a slow image never
    // stalls the swap.
    function waitForImageReady(html) {
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('[id^="productTemplate--"], [id^="MainProduct-"]');
        if (!node) return Promise.resolve(null);
        const img = node.querySelector(
          '.product-gallery__media img, .product-gallery__media-slide img, ' +
          '.product__media img, [id^="productTemplate--"] img, [id^="MainProduct-"] img'
        );
        if (!img) return Promise.resolve(node);
        const dataSrc = img.getAttribute('data-src') || '';
        const dataSrcset = img.getAttribute('data-srcset') || '';
        const src = img.getAttribute('src') || '';
        const srcset = img.getAttribute('srcset') || '';
        const pickFirst = function (s) {
          if (!s) return '';
          return (s.indexOf(',') >= 0 ? s.split(',')[0] : s).trim().split(' ')[0];
        };
        const url = dataSrc || pickFirst(dataSrcset) || src || pickFirst(srcset);
        if (!url) return Promise.resolve(node);
        return new Promise(function (resolve) {
          const probe = new Image();
          let done = false;
          function finish() { if (!done) { done = true; resolve(node); } }
          probe.onload = probe.onerror = finish;
          probe.src = url;
          // If already cached, .complete is true synchronously
          if (probe.complete) finish();
          // Tighter ceiling — prefetch should have cached the image
          // before the user clicks. If not, 80ms is enough headroom.
          setTimeout(finish, 80);
        });
      } catch (e) {
        return Promise.resolve(null);
      }
    }

    htmlPromise
      .then(waitForImageReady)
      .then(function (newProductInfo) {
        if (!newProductInfo) {
          window.location.href = pathname;
          return;
        }

        const currentProductInfo = findActiveProductInfo();
        if (!currentProductInfo) {
          window.location.href = pathname;
          return;
        }

        // Perform the actual swap. Wrapped in startViewTransition when
        // available so the browser captures snapshots of both states
        // and cross-fades automatically — no manual fade math, no blank
        // gap, no white/black flash.
        function doSwap() {
          // -- PREVENT FLASH: Manually trigger animations on the new section BEFORE inserting it into the DOM
          if (newProductInfo.hasAttribute('data-aos-intersection')) {
            newProductInfo.classList.add('aos-animate');
          }
          newProductInfo.querySelectorAll('[data-aos]').forEach(function(el) {
            el.classList.add('aos-init', 'aos-animate');
            el.style.transition = 'none';
            el.style.animation = 'none';
          });

          // -- PREVENT IMAGE FLICKER: Disable lazy-loading on every gallery
          // image so the whole gallery paints instantly (the prefetch step
          // already warmed the browser cache for these URLs).
          const newGalleryImgs = newProductInfo.querySelectorAll(
            '.product-gallery__media img, .product-gallery__media-slide img, .product__media img, [id^="productTemplate--"] img'
          );
          newGalleryImgs.forEach(function (newMainImg) {
            newMainImg.removeAttribute('loading');
            if (newMainImg.hasAttribute('data-src')) {
              newMainImg.src = newMainImg.getAttribute('data-src');
            }
            if (newMainImg.hasAttribute('data-srcset')) {
              newMainImg.srcset = newMainImg.getAttribute('data-srcset');
            }
            newMainImg.classList.add('lazyloaded');
            newMainImg.classList.remove('lazyload', 'lazyload--loading');
            // CRITICAL anti-blink: Foxify's lazy-image starts at opacity 0
            // and fades in via transition. At swap time the new image is
            // mid-fade (~0), so the visible preview is replaced by a
            // near-invisible image → the "blink and back" the client saw.
            // Pin it fully opaque with no transition/animation so the
            // swapped-in gallery is paint-identical to the instant preview.
            newMainImg.style.opacity = '1';
            newMainImg.style.transition = 'none';
            newMainImg.style.animation = 'none';
          });

          // Same treatment for the lazy-image WRAPPERS (the fade often
          // lives on the holder, not the <img> itself).
          newProductInfo.querySelectorAll(
            '.product-gallery__media, .product-gallery__media-slide, .lazy-image, [data-product-media]'
          ).forEach(function (el) {
            el.style.opacity = '1';
            el.style.transition = 'none';
            el.style.animation = 'none';
            el.classList.add('lazyloaded');
            el.classList.remove('lazyload', 'lazyload--loading');
          });

          // -- SYNC SWATCH STATE --
          // Foxify's server might return the new HTML without the active swatch marked,
          // or with the wrong one marked. We forcefully mark it so `custom.js` finds it.
          const newSwatches = newProductInfo.querySelectorAll('.siblings a');
          newSwatches.forEach(function(a) {
            const href = a.getAttribute('href') || '';
            if (href.indexOf(pathname) !== -1) {
              a.setAttribute('aria-current', 'true');
            } else {
              a.removeAttribute('aria-current');
            }
          });

          // -- PREVENT WRONG ACTIVE COLOR --
          // Directly set the Chivio active color variables so there is zero layout lag
          let siblingColor = (link.getAttribute('data-sibling-color') || '').trim();
          if (!siblingColor) siblingColor = (link.style.getPropertyValue('--swatch') || '').trim();
          if (!siblingColor) {
            const childWithSwatch = link.querySelector('[style*="--swatch"]');
            if (childWithSwatch) siblingColor = childWithSwatch.style.getPropertyValue('--swatch').trim();
          }
          if (siblingColor) {
            newProductInfo.style.setProperty('--chivio-active-color', siblingColor);
            newProductInfo.querySelectorAll('fieldset, .radio__fieldset, .radio__buttons').forEach(function(fs) {
              fs.style.setProperty('--chivio-active-color', siblingColor);
            });
            document.documentElement.style.setProperty('--chivio-active-color', siblingColor);
          }

          history.replaceState({}, '', pathname);
          currentProductInfo.replaceWith(newProductInfo);
          // Reset any opacity styles from previous-pass code
          newProductInfo.style.opacity = '';
          newProductInfo.style.transition = '';

          // Force gallery (Flickity) + GSAP ScrollTrigger to re-measure
          // after the new product-info is in the DOM.
          // 1. Immediate trigger for fast browsers
          window.dispatchEvent(new Event('resize'));
          
          // 2. Delayed trigger to ensure all custom elements (like Flickity) 
          // have had a frame to mount and calculate their heights.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              window.dispatchEvent(new Event('resize'));
              if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
                ScrollTrigger.refresh();
              }
            });
          });
        }

        // Wrap in startViewTransition to freeze the screen while the new DOM
        // is parsed and custom elements (like Flickity) mount. This prevents
        // any white flashes or layout collapsing.
        if (document.startViewTransition) {
          document.startViewTransition(function() {
            return new Promise(function(resolve) {
              doSwap();
              // -- HIDE FLICKITY INITIALIZATION --
              // Delay taking the new snapshot to give Foxify's Flickity gallery
              // enough time to rebuild its DOM and stabilize. This completely 
              // hides the "double flash" or blank gap caused by gallery scripts.
              setTimeout(resolve, 80);
            });
          });
        } else {
          doSwap();
        }

        // Prefetch the new page's siblings so subsequent clicks stay instant.
        scheduleEagerPrefetch();
      })
      .catch(function (err) {
        console.warn(LOG_PREFIX, 'fetch/swap failed, hard nav', err);
        window.location.href = pathname;
      })
      .finally(function () {
        swapping = false;
      });
  });

})();
